/* ===========================================================================
 * WHERE A PORTALLED SURFACE LANDS — `document.body`, except when the page is
 * not what the screen is showing.
 *
 * Three surfaces in this package escape their parent by portalling out of it:
 * `FiltersPopover`'s panel, `Combobox`'s listbox and `RowActions`' menu. All
 * three targeted `document.body`, which is right for every screen but one.
 *
 * THE FULLSCREEN API DOES NOT RENDER THE REST OF THE DOCUMENT. While an element
 * is fullscreen the browser paints that element's subtree and nothing else — a
 * `position: fixed` div hanging off `document.body` is not "behind" the page, it
 * is not drawn at all, and no z-index reaches it. `/operations` is the app's one
 * `requestFullscreen()` caller (the wall board), and its roster rail carries a
 * search box and the compact Filters trigger: press the trigger on a full-screen
 * board and the panel opens — `aria-expanded` flips, the focus trap activates,
 * every test still passes — and the reader sees nothing happen.
 *
 * That is the same family as the two clipping defects these portals were
 * introduced to fix (§4 of docs/design-rules.md: a child that escapes, or fails
 * to escape, a context its parent never declared for it). The answer is the same
 * shape too: ask at open time which subtree is actually on screen, rather than
 * assuming it is the document's.
 *
 * `position: fixed` inside the fullscreen element still positions against the
 * viewport, and a plain `overflow: hidden` ancestor does not clip a fixed
 * descendant, so nothing about the placement arithmetic changes.
 * ======================================================================== */

/**
 * The node a floating surface should be portalled into: the element currently
 * fullscreen, or `document.body` when nothing is.
 *
 * Called during render, so a panel opened before and after entering fullscreen
 * lands in the right subtree either way.
 */
export function portalHost(): HTMLElement {
  const full = typeof document === 'undefined' ? null : document.fullscreenElement;
  return full instanceof HTMLElement ? full : document.body;
}
